import { useCallback, useEffect, useRef, useState } from "react";
import { createEngine, TEMPLATE_VERSION } from "../mockup-engine";

/** Longest edge of the interactive preview template. Exports are full size. */
export function previewSize() {
  if (typeof window === "undefined") return 1600;
  return window.innerWidth < 768 ? 1200 : 1600;
}

export function isLowMemoryDevice() {
  if (typeof navigator === "undefined") return false;
  return (navigator.deviceMemory && navigator.deviceMemory <= 4) || window.innerWidth < 768;
}

/** Preview templates kept in memory for instant switching between mockups. */
function cacheSize() {
  return isLowMemoryDevice() ? 0 : 3;
}

const INITIAL = { phase: "idle", template: null, draft: false, progress: 0, label: "", error: null, exportReady: false, info: null, source: null };

/** Stored template URLs of a listing, when they match this engine's format. */
export function storedTemplates(listing) {
  if (!listing?.template_preview || Number(listing.template_version) !== TEMPLATE_VERSION) return null;
  return { previewUrl: listing.template_preview, fullUrl: listing.template_full || null };
}

/**
 * Opens a listing for full-resolution export in `engine` under `docId`
 * (stored full template when available, otherwise the PSD). Resolves to
 * { designWidth, designHeight, ... }.
 */
export async function openForExport(engine, docId, listing, onProgress) {
  const stored = storedTemplates(listing);
  if (stored?.fullUrl) return (await engine.openTemplate(docId, { fullUrl: stored.fullUrl, psdUrl: listing.json_file || null }, onProgress)).info;
  if (!listing?.json_file) throw new Error("This mockup has no Photoshop file.");
  return engine.open(docId, { url: listing.json_file, skipLinkedFiles: true }, onProgress);
}

/**
 * Loads a listing's mockup template for the editor.
 *
 * 1. Stored template (`template_preview`, made when the PSD was uploaded):
 *    a few MB, ready immediately. Downloads use `template_full`.
 * 2. Otherwise (older listings, or if the stored files fail) the PSD is
 *    converted in the browser: the small `compressed_json_file` gives a quick
 *    draft, then the full `json_file` replaces it with a sharp template.
 * The full-resolution export template is warmed in the background.
 *
 * Recently opened preview templates stay cached, so switching back to a
 * mockup is instant; its export document is then opened on demand
 * (`prepareExport`). Only the active listing keeps a worker. Changing
 * `reloadKey` retries a failed load.
 */
export function useMockupTemplate(mockupData, reloadKey = 0) {
  const [state, setState] = useState(INITIAL);
  const engineRef = useRef(null);
  const exportRef = useRef(null); // Promise of the open "full" export document
  const cacheRef = useRef(new Map()); // sourceKey -> { template, info }
  const listingRef = useRef(mockupData);
  listingRef.current = mockupData;
  const psdUrl = mockupData?.json_file || null;
  const draftUrl = mockupData?.compressed_json_file || null;
  const stored = storedTemplates(mockupData);
  const previewUrl = stored?.previewUrl || null;
  const fullTemplateUrl = stored?.fullUrl || null;
  const sourceKey = previewUrl || psdUrl;

  useEffect(() => {
    if (!sourceKey) {
      // No source (e.g. the next listing is still loading): never keep
      // showing the previous template.
      setState(INITIAL);
      return undefined;
    }
    const engine = createEngine();
    engineRef.current = engine;
    exportRef.current = null;
    let cancelled = false, finalShown = false;
    const update = (patch) => !cancelled && setState((s) => ({ ...s, ...patch, url: sourceKey }));
    const remember = (template, info) => {
      const cache = cacheRef.current, limit = cacheSize();
      cache.delete(sourceKey);
      if (!limit) return;
      cache.set(sourceKey, { template, info });
      while (cache.size > limit) cache.delete(cache.keys().next().value);
    };

    const warmExport = () => {
      // Low-memory devices load the full template on the first download
      // instead (a 3000 px template needs ~225 MB). A failure here is retried
      // by the first export, so it is not fatal.
      if (isLowMemoryDevice()) {
        update({ exportReady: true });
        return;
      }
      (exportRef.current || Promise.resolve())
        .then(() => engine.prepareExport("full"))
        .then(() => update({ exportReady: true }))
        .catch((error) => {
          if (!cancelled) console.warn("Export template warm-up failed", error);
        });
    };

    const showReady = (template, source, info = null) => {
      if (cancelled) return;
      finalShown = true;
      remember(template, info);
      update({ phase: "ready", template, draft: false, progress: 1, label: "", source, info: info || undefined });
      warmExport();
    };

    const fail = (error) => {
      if (cancelled) return;
      console.error("Mockup template failed", error);
      update({ phase: "error", error: error?.message || "This mockup could not be opened." });
    };

    const convertPsd = () => {
      if (!psdUrl) {
        fail(new Error("This mockup has no Photoshop file."));
        return;
      }
      if (draftUrl) {
        engine
          .open("draft", { url: draftUrl, skipLinkedFiles: true })
          .then(() => engine.compile("draft", Infinity))
          .then((template) => {
            if (!cancelled && !finalShown) update({ phase: "draft", template, draft: true });
          })
          .catch(() => {})
          .finally(() => engine.close("draft").catch(() => {}));
      }
      let info = null;
      exportRef.current = engine
        .open("full", { url: psdUrl, skipLinkedFiles: true }, (progress, label) => update({ progress: progress * 0.85, label }))
        .then((result) => {
          info = result;
          return result;
        });
      exportRef.current
        .then((result) => {
          update({ info: result, label: "Preparing your mockup…" });
          return engine.compile("full", previewSize(), (p, label) => update({ progress: 0.85 + p * 0.15, label }));
        })
        .then((template) => showReady(template, "psd", info))
        .catch(fail);
    };

    const cached = cacheRef.current.get(sourceKey);
    if (cached) {
      // Instant: the export document opens in the background.
      setState({ ...INITIAL, url: sourceKey, phase: "ready", template: cached.template, info: cached.info, progress: 1, source: "cache" });
      finalShown = true;
      if (!isLowMemoryDevice() && fullTemplateUrl) {
        exportRef.current = openForExport(engine, "full", listingRef.current);
        exportRef.current.then(() => update({ exportReady: true })).catch((error) => console.warn("Export template warm-up failed", error));
      } else update({ exportReady: true });
    } else {
      setState({ ...INITIAL, url: sourceKey, phase: "loading", label: "Downloading mockup…" });
      if (previewUrl) {
        exportRef.current = engine.openTemplate("full", { previewUrl, fullUrl: fullTemplateUrl, psdUrl }, (progress, label) => update({ progress, label }));
        exportRef.current
          .then(({ template }) => showReady(template, "stored"))
          .catch((error) => {
            if (cancelled) return;
            console.warn("Stored mockup template unavailable; converting the PSD instead.", error);
            convertPsd();
          });
      } else {
        convertPsd();
      }
    }

    return () => {
      cancelled = true;
      engine.dispose();
      engineRef.current = null;
      exportRef.current = null;
    };
  }, [sourceKey, psdUrl, draftUrl, previewUrl, fullTemplateUrl, reloadKey]);

  /** Makes sure the active engine can export ("full" document open). */
  const prepareExport = useCallback(async (onProgress) => {
    const engine = engineRef.current;
    if (!engine) throw new Error("The mockup is still loading.");
    if (!exportRef.current) exportRef.current = openForExport(engine, "full", listingRef.current, onProgress);
    try {
      await exportRef.current;
    } catch (error) {
      // Retry once from scratch (e.g. a network error while warming).
      if (engineRef.current !== engine) throw error;
      exportRef.current = openForExport(engine, "full", listingRef.current, onProgress);
      await exportRef.current;
    }
    return engine;
  }, []);

  // State always belongs to the current source, even before the effect runs.
  const current = state.url === sourceKey && sourceKey ? state : INITIAL;
  return { ...current, engineRef, prepareExport };
}
